// src/components/admin/category/PhaseModal.tsx
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  phaseSchema,
  PhaseFormValues,
  statusFaseEnum,
  statusCategoriaEnum,
} from '../../../schemas/category/categorySchemas';
import { criarFase, atualizarFase } from '../../../services/faseCategoriaTorneioService';
import { Snackbar, Alert } from '@mui/material';

export interface PhaseModalProps {
  open: boolean;
  initialValues?: Partial<PhaseFormValues>;
  onClose?: () => void;
  onSaved?: (values: PhaseFormValues) => void;
}

const PhaseModal: React.FC<PhaseModalProps> = ({ open, initialValues, onClose, onSaved }) => {
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const handleCloseSnackbar = () => setSnackbar(s => ({ ...s, open: false }));

  const [isSaving, setIsSaving] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PhaseFormValues>({
    resolver: zodResolver(phaseSchema),
    defaultValues: {
      id: '',
      categoriaTorneioId: '',
      nome: '',
      dataInicio: new Date(),
      dataFim: new Date(),
      status: statusFaseEnum.enum.AGENDADA,
      ordem: 0,
      tipoGrupoChave: undefined,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        id: initialValues?.id || '',
        categoriaTorneioId: initialValues?.categoriaTorneioId || '',
        nome: initialValues?.nome || '',
        dataInicio: initialValues?.dataInicio || new Date(),
        dataFim: initialValues?.dataFim || new Date(),
        status: initialValues?.status || statusFaseEnum.enum.AGENDADA,
        ordem: initialValues?.ordem || 0,
        tipoGrupoChave: initialValues?.tipoGrupoChave
      });
      setTimeout(() => {
        document.getElementById('nome')?.focus();
      }, 0);
    }
  }, [open, initialValues, reset]);

  if (!open) return null;

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && onClose) onClose();
  };

  const onSubmit = handleSubmit(async (values, e) => {
    e?.preventDefault();
    // setIsSaving(true);
    // try {
    //   if (values.id) {
    //     await atualizarFase(values.id, {
    //       categoriaTorneioId: values.categoriaTorneioId,
    //       nome: values.nome,
    //       dataInicio: values.dataInicio,
    //       dataFim: values.dataFim,
    //       status: values.status,
    //       ordem: values.ordem,
    //     });
    //   } else {
    //     await criarFase({
    //       categoriaTorneioId: values.categoriaTorneioId,
    //       nome: values.nome,
    //       dataInicio: values.dataInicio,
    //       dataFim: values.dataFim,
    //       status: values.status,
    //       ordem: values.ordem,
    //     });
    //   }
    //   setSnackbar({ open: true, message: 'Fase salva com sucesso', severity: 'success' });
      onSaved?.(values);
      onClose?.();
    // } catch (err) {
    //   console.error(err);
    //   setSnackbar({ open: true, message: 'Falha ao salvar a fase', severity: 'error' });
    // } finally {
    //   setIsSaving(false);
    // }
  });

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show d-block" role="dialog" tabIndex={-1} onMouseDown={handleBackdrop}>
        <div className="modal-dialog" role="document">
          <form className="modal-content" onSubmit={onSubmit} noValidate>
            <div className="modal-header">
              <h5 className="modal-title">Fase</h5>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label" htmlFor="nome">Nome</label>
                <input id="nome" className="form-control" aria-label="Nome" autoFocus {...register('nome')} />
                {errors.nome && <div className="text-danger small">{errors.nome.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="dataInicio">Data início</label>
                <input
                  type="datetime-local"
                  id="dataInicio"
                  className="form-control"
                  aria-label="Data início"
                  {...register('dataInicio', { valueAsDate: true })}
                />
                {errors.dataInicio && <div className="text-danger small">{errors.dataInicio.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="dataFim">Data fim</label>
                <input
                  type="datetime-local"
                  id="dataFim"
                  className="form-control"
                  aria-label="Data fim"
                  {...register('dataFim', { valueAsDate: true })}
                />
                {errors.dataFim && <div className="text-danger small">{errors.dataFim.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="status">Status</label>
                <select id="status" className="form-select" aria-label="Status" {...register('status')}>
                  {Object.values(statusFaseEnum.enum).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {errors.status && <div className="text-danger small">{errors.status.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="tipoGrupoChave">Tipo Grupo/Chave</label>
                <select id="tipoGrupoChave" className="form-select" aria-label="Tipo Grupo/Chave" {...register('tipoGrupoChave')}>
                  <option value="GRUPO">GRUPO</option>
                  <option value="CHAVE">CHAVE</option>
                </select>
                {errors.tipoGrupoChave && <div className="text-danger small">{errors.tipoGrupoChave.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="ordem">Ordem</label>
                <input
                  type="number"
                  id="ordem"
                  className="form-control"
                  aria-label="Ordem"
                  {...register('ordem', { valueAsNumber: true })}
                />
                {errors.ordem && <div className="text-danger small">{errors.ordem.message}</div>}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSaving}
                data-testid="phase-modal-save-button"
              >
                {isSaving && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                Salvar
              </button>
            </div>
          </form>
        </div>
      </div>

      {/** Feedback via MUI Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </>
  );
};

export default PhaseModal;
